import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { OrderService } from '@admin-core/services/order.service';
import {
  BundleOptionSnapshot,
  Order,
  OrderListItem,
  OrderStatus
} from '@admin-core/models/order.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { formatMoney } from '@admin-shared/utils/money.util';
import { formatEasternDateTime } from '@admin-shared/utils/date-time.util';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-orders-admin',
  standalone: true,
  imports: [FormsModule, DatePipe, TranslatePipe],
  templateUrl: './orders-admin.component.html',
  styleUrl: './orders-admin.component.scss'
})
export class OrdersAdminComponent implements OnInit {
  private readonly api = inject(OrderService);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<OrderListItem[]>([]);
  readonly totalCount = signal(0);
  readonly loading = signal(false);
  readonly detailOpen = signal(false);
  readonly detailLoading = signal(false);
  readonly downloadingPdfId = signal<string | null>(null);
  readonly selected = signal<Order | null>(null);
  readonly search = signal('');

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api
      .getPaged({
        search: this.search().trim() || undefined,
        status: 'Paid',
        page: 1,
        pageSize: 100,
        sort: 'createdAt_desc'
      })
      .subscribe({
        next: (page) => {
          this.items.set(page.items);
          this.totalCount.set(page.totalCount);
          this.loading.set(false);
        },
        error: (err) => {
          this.showError(err?.error?.error ?? this.i18n.t('orders.loadError'));
          this.loading.set(false);
        }
      });
  }

  onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.load();
  }

  openDetail(item: OrderListItem): void {
    this.detailOpen.set(true);
    this.detailLoading.set(true);
    this.selected.set(null);
    this.api.getById(item.id).subscribe({
      next: (order) => {
        this.selected.set(order);
        this.detailLoading.set(false);
      },
      error: (err) => {
        this.showError(err?.error?.error ?? this.i18n.t('orders.loadError'));
        this.detailLoading.set(false);
        this.detailOpen.set(false);
      }
    });
  }

  closeDetail(): void {
    this.detailOpen.set(false);
    this.selected.set(null);
  }

  isDownloadingPdf(orderId: string): boolean {
    return this.downloadingPdfId() === orderId;
  }

  downloadPdf(order: Order): void {
    this.startPdfDownload(order.id, order.orderNumber || order.id);
  }

  downloadPdfFromList(item: OrderListItem): void {
    this.startPdfDownload(item.id, item.orderNumber || item.id);
  }

  private startPdfDownload(orderId: string, orderRef: string): void {
    if (this.downloadingPdfId()) {
      return;
    }

    this.downloadingPdfId.set(orderId);
    this.api.downloadAdminPdf(orderId).subscribe({
      next: (blob) => {
        if (!this.isPdfBlob(blob)) {
          this.showError(this.i18n.t('portal.checkout.pdfError'));
          this.downloadingPdfId.set(null);
          return;
        }
        this.savePdfBlob(blob, orderRef);
        this.downloadingPdfId.set(null);
      },
      error: () => {
        this.showError(this.i18n.t('portal.checkout.pdfError'));
        this.downloadingPdfId.set(null);
      }
    });
  }

  private isPdfBlob(blob: Blob | null | undefined): boolean {
    if (!blob || blob.size === 0) {
      return false;
    }
    if (blob.type && blob.type.includes('html')) {
      return false;
    }
    return !blob.type || blob.type.includes('pdf') || blob.type === 'application/octet-stream';
  }

  private savePdfBlob(blob: Blob, orderRef: string): void {
    const shortId = orderRef.replace(/-/g, '').slice(0, 8).toUpperCase();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Order-${shortId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  money(value: number): string {
    return formatMoney(value);
  }

  formatPaidAt(value: string): string {
    return formatEasternDateTime(value);
  }

  customerName(item: Pick<OrderListItem, 'customerFirstName' | 'customerLastName'>): string {
    return `${item.customerFirstName} ${item.customerLastName}`.trim();
  }

  statusLabel(status: OrderStatus): string {
    const map: Record<OrderStatus, 'orders.status.Pending' | 'orders.status.Paid'> = {
      Pending: 'orders.status.Pending',
      Paid: 'orders.status.Paid'
    };
    return this.i18n.t(map[status]);
  }

  parseBundleOptions(json: string | null): BundleOptionSnapshot[] {
    if (!json) {
      return [];
    }
    try {
      const parsed = JSON.parse(json) as BundleOptionSnapshot[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
